// src/components/common/WorkflowApprovalModal.js
import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, GitBranch, User, Clock } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import flowAutomationService from '../../services/flowAutomation.service';

const WorkflowApprovalModal = ({ executionId, onClose, onResolved }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [execution, setExecution] = useState(null);
    const [comments, setComments] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const result = await flowAutomationService.getExecution(executionId);
                if (result.success !== false) {
                    setExecution(result.execution);
                } else {
                    setError(result.error || 'Failed to load execution');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        if (executionId) load();
    }, [executionId]);

    const handleResolve = async (approved) => {
        if (!approved && !comments.trim()) {
            alert('Please provide a reason for rejection');
            return;
        }

        setSubmitting(true);
        try {
            const result = await flowAutomationService.resolveApproval(executionId, approved, comments);
            if (result.success !== false) {
                if (onResolved) onResolved(approved);
                onClose();
            } else {
                alert(`Failed: ${result.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const triggerData = execution?.trigger_data
        ? (typeof execution.trigger_data === 'string' ? JSON.parse(execution.trigger_data) : execution.trigger_data)
        : {};
    const employeeName = triggerData?.employee?.name || 'Employee';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className={`w-full max-w-lg rounded-xl shadow-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                            <GitBranch className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Workflow Approval
                            </h2>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Review and approve this workflow step
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500" />
                        </div>
                    ) : error ? (
                        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                            {error}
                        </div>
                    ) : execution ? (
                        <div className="space-y-4">
                            {/* Workflow */}
                            <div>
                                <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Workflow
                                </label>
                                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {execution.workflow_name || 'Untitled'}
                                </p>
                            </div>

                            {/* Triggered by */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`text-xs font-medium flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <User className="w-3 h-3" /> Employee
                                    </label>
                                    <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                        {employeeName}
                                    </p>
                                </div>
                                <div>
                                    <label className={`text-xs font-medium flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <Clock className="w-3 h-3" /> Started
                                    </label>
                                    <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                        {execution.started_at
                                            ? new Date(execution.started_at).toLocaleString()
                                            : '—'}
                                    </p>
                                </div>
                            </div>

                            {/* Context details (human-readable) */}
                            {(() => {
                                const rows = [];
                                const emp = triggerData?.employee;
                                if (emp?.designation) rows.push({ label: 'Designation', value: emp.designation });
                                if (emp?.email) rows.push({ label: 'Email', value: emp.email });
                                if (emp?.employee_code) rows.push({ label: 'Employee Code', value: emp.employee_code });

                                if (triggerData?.leave) {
                                    const l = triggerData.leave;
                                    if (l.type) rows.push({ label: 'Leave Type', value: l.type });
                                    if (l.days) rows.push({ label: 'Days', value: String(l.days) });
                                    if (l.from) rows.push({ label: 'From', value: l.from });
                                    if (l.to) rows.push({ label: 'To', value: l.to });
                                }

                                if (triggerData?.attendance) {
                                    const a = triggerData.attendance;
                                    if (a.date) rows.push({ label: 'Date', value: a.date });
                                    if (a.type) rows.push({ label: 'Type', value: String(a.type).replace(/_/g, ' ') });
                                }

                                if (triggerData?.goal?.title) rows.push({ label: 'Goal', value: triggerData.goal.title });
                                if (triggerData?.task?.title) rows.push({ label: 'Task', value: triggerData.task.title });

                                if (rows.length === 0) return null;
                                return (
                                    <div>
                                        <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Details
                                        </label>
                                        <div className={`text-xs rounded-lg p-3 mt-1 space-y-1.5 ${
                                            isDark ? 'bg-gray-900/50' : 'bg-gray-50'
                                        }`}>
                                            {rows.map((row, i) => (
                                                <div key={i} className="flex gap-2">
                                                    <span className={`font-medium min-w-[100px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        {row.label}:
                                                    </span>
                                                    <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                                                        {row.value}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Comments */}
                            <div>
                                <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Comments (required for rejection)
                                </label>
                                <textarea
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    placeholder="Add a note about your decision..."
                                    className={`mt-1 w-full px-3 py-2 rounded-lg text-sm border resize-y min-h-[80px] ${
                                        isDark
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                                    } focus:ring-2 focus:ring-purple-500 focus:outline-none`}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                {execution && !loading && (
                    <div className={`flex justify-end gap-2 px-5 py-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                            onClick={() => handleResolve(false)}
                            disabled={submitting}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-900/20 disabled:opacity-50"
                        >
                            <XCircle className="w-4 h-4" />
                            Reject
                        </button>
                        <button
                            onClick={() => handleResolve(true)}
                            disabled={submitting}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        >
                            <CheckCircle className="w-4 h-4" />
                            {submitting ? 'Processing...' : 'Approve'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkflowApprovalModal;
